/* eslint-disable react/no-unused-prop-types */
import { memo } from 'react';
import styled from 'styled-components';
import NextImage from 'next/image';
import { useRouter } from 'next/router';
import {
  Controller,
  ControllerRenderProps,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import NumberFormat, { NumberFormatValues } from 'react-number-format';
import cx from 'classnames';

import Title from 'components/common/title';
import Text from 'components/common/text';
import { device } from 'styles/breakpoints';
import { Fields } from 'types/fields';
import { listPriceSchema } from 'utils/validations';
import { Button } from 'components/common/button';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { changeStep, fillListing } from 'store/slices/onboarding';
import { onboardingSelector } from 'store/slices/onboarding/selector';

function PriceForm() {
  const dispatch = useAppDispatch();
  const { listing } = useAppSelector(onboardingSelector);
  const { query } = useRouter();
  const {
    handleSubmit,
    formState: { isValid },
    control,
  } = useForm<Fields>({
    mode: 'onChange',
    resolver: yupResolver(listPriceSchema),
  });

  const onSubmit: SubmitHandler<Fields> = async (data) => {
    dispatch(
      fillListing({
        listPrice: Number(data.listPrice),
        agentCommission: Number(data.agentCommission),
      })
    );
    dispatch(changeStep('stepConfirm'));
  };

  return (
    <>
      <PriceFormHead>
        <NextImage src="/static/icons/listing-1.svg" width={100} height={100} />
        <Title type="mode3" fw="light" className="price-form-title">
          One Step Closer to
        </Title>
        <Title type="mode3" fw="bold" className="price-form-subtitle">
          Selling Your Home
        </Title>
        <Text type="mode4" fw="bold" className="price-form-address">
          {`"${query.address}"`}
        </Text>
        <Text type="mode8" className="price-form-info">
          Tell us the asking price and Buyers Commission. You can change these
          later.
        </Text>
      </PriceFormHead>

      <ListPriceForm onSubmit={handleSubmit(onSubmit)}>
        <div className="list-price-inp">
          <Controller
            defaultValue={
              listing?.listPrice ? String(listing?.listPrice) : undefined
            }
            control={control}
            name="listPrice"
            render={renderListingPrice}
          />
        </div>
        <div className="agent-com-inp">
          <Controller
            defaultValue={
              listing?.agentCommission
                ? String(listing?.agentCommission)
                : undefined
            }
            control={control}
            name="agentCommission"
            render={renderAgentCommission}
          />
        </div>

        <Button type="submit" className="submit" disabled={!isValid}>
          <Text type="mode3">Continue</Text>
        </Button>
      </ListPriceForm>
    </>
  );
}

function renderListingPrice({
  field: { onChange, name, value },
}: RenderListingProps) {
  function handleValueChange(values: NumberFormatValues) {
    const { value: updatedValue } = values;
    onChange(updatedValue);
  }

  return (
    <NumberFormat
      placeholder="Asking Price"
      name={name}
      value={value}
      onValueChange={handleValueChange}
      className={cx({
        valid: Boolean(value),
      })}
      thousandSeparator
      prefix="$ "
      maxLength={10}
    />
  );
}

function renderAgentCommission({
  field: { onChange, name, value },
}: RenderAgentCommissionProps) {
  function handleValueChange(values: NumberFormatValues) {
    const { value: updatedValue } = values;
    onChange(updatedValue);
  }

  function handleIsAllowed(values: NumberFormatValues) {
    const { value: updatedValue } = values;
    if (Number(updatedValue) <= 10) return true;
    return false;
  }

  return (
    <NumberFormat
      placeholder="Buyers Agent Commission"
      name={name}
      value={value}
      onValueChange={handleValueChange}
      className={cx({
        valid: Boolean(value),
      })}
      suffix=" %"
      isAllowed={handleIsAllowed}
      maxLength={5}
    />
  );
}

interface RenderListingProps {
  field: ControllerRenderProps<Fields, 'listPrice'>;
}

interface RenderAgentCommissionProps {
  field: ControllerRenderProps<Fields, 'agentCommission'>;
}

export const PriceFormHead = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  .price-form {
    &-title {
      margin-top: 36px;
    }
    &-subtitle,
    &-address {
      margin: 8px 0;
    }
    &-info {
      margin: 24px 0;
      text-align: center;
      color: ${({ theme }) => theme.colors.bluePallet1};
    }
  }
  @media ${device.smallLaptop} {
    .price-form {
      &-title,
      &-subtitle {
        font-size: 28px;
        line-height: 36px;
      }
    }
  }
`;

const ListPriceForm = styled.form`
  .list-price-inp,
  .agent-com-inp {
    margin-top: 16px;
    > input {
      width: 100%;
      height: 70px;
      text-align: center;
      color: ${({ theme }) => theme.colors.bluePallet1};
      font-size: 18px;
      line-height: 20px;
      font-family: 'Walsheim-Regular';
      letter-spacing: 2px;
      border: 1px solid ${({ theme }) => theme.colors.grayPallet5};
      background-color: ${({ theme }) => theme.colors.grayPallet5};

      &.valid,
      &:focus {
        border: 1px solid ${({ theme }) => theme.colors.green};
        background-color: transparent;
      }
    }
  }
  .submit {
    margin-top: 36px;
    border-radius: unset;
    &:disabled {
      opacity: 0.4;
    }
  }
`;

export default memo(PriceForm);
